import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

import { isCashout, isCopy, isPay, isSubmitBigWin } from "@tickets/helpers/actions/isActionAvailable";
import { isTicketPayPeriodExpired, payTicket } from "@tickets/components/cardView/helper";
import { copyToBetslip } from "@tickets/helpers/actions/copyToBetslip";
import { getTicketWinAmount } from "@tickets/helpers/getTicketWinAmount";
import { getTicketStateName } from "@tickets/helpers/ticketState";
import { openDeferModal } from "@tickets/helpers/openDeferPopup";
import { earlyCashout } from "@tickets/helpers/actions/cashout";
import { getTicketIndex } from "@tickets/helpers/actions/pay";
import ActionSquare from "@common/components/actionSquare";
import ToastWrapper from "@common/helpers/toastWrapper";
import { PopupContext } from "@app/dev/contexts/popup";
import { POPUPS } from "@common/constants/popups";
import useTicketsSync from "@tickets/hooks";
import Config from "@skin_config";
import { getCasinoCopyBetData } from "@tickets/helpers/mappers";

const TicketDetailsAction = ({ ticket, setTickets, tickets, cashoutAmount, onCashoutAmountChange }) => {
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const [canPayTicket, setCanPayTicket] = useState(true);

	const { dataFromSwarm } = ticket;
	const { isPayable } = getTicketStateName(ticket);
	const [{ openPopup, closePopup }] = useContext(PopupContext);

	useTicketsSync(0, onCashoutAmountChange, setLoading);

	const sportTicketCashoutAmount = dataFromSwarm?.is_live
		? cashoutAmount || 0
		: dataFromSwarm?.cashout_amount || 0;

	const cashout = async () => {
		if (loading) return;

		// openPopup(POPUPS.PROCESS_POPUP, {
		// 	title: "Cashout in process",
		// 	icon: "cashout",
		// 	content: `Your cashout request is being processed.
		// Please note that the cashout amount may change during the process.`,
		// });

		setLoading(true);
		const cashoutResult = await earlyCashout(ticket, sportTicketCashoutAmount);

		if (!cashoutResult) {
			setLoading(false);
			return;
		}

		const { success } = cashoutResult;

		if (success) ToastWrapper("success", "Successfully cashout");
		else ToastWrapper("error", "Cashout bet failed!");

		setTimeout(() => {
			setLoading(false);
		}, 2000);
	};

	const submitBigWin = async () => {
		if (isTicketPayPeriodExpired(ticket)) return;

		const isSuccess = await openDeferModal(
			POPUPS.REQUEST_PAYOUT,
			{ betId: dataFromSwarm?.Id ?? dataFromSwarm.id, popupPosition: "top", isCasino: !!ticket?.dataFromCasino?.TransactionId, winId: dataFromSwarm?.WinId, currencyId: dataFromSwarm?.currency_id },
			false
		);

		if (!isSuccess) return;

		const ticketIndex = getTicketIndex(ticket, tickets);
		const updatedTickets = [...tickets[ticket.ticketType]];

		updatedTickets[ticketIndex] = { ...ticket, paid: true };
		if (ticketIndex !== -1) {
			setTickets({
				...tickets,
				[ticket.ticketType]: updatedTickets,
			});
		}
	};

	const addBalance = async () => {
		// make call per 2 seconds
		if (isTicketPayPeriodExpired(ticket)) return;
		if (loading || !canPayTicket) return;

		setLoading(true);
		setCanPayTicket(false);

		setTimeout(() => {
			setCanPayTicket(true);
		}, 2000); // fix for ddosing button

		openPopup(POPUPS.PROCESS_POPUP, {
			title: "Pay in process",
			icon: "pay",
			content: "Pay is in progress. Please wait while the process is completed.",
		});

		const [data] = await payTicket([ticket]);

		if (data?.Result?.Succeeded) {
			const ticketIndex = getTicketIndex(ticket, tickets);
			const updatedTickets = [...tickets[ticket.ticketType]];

			updatedTickets[ticketIndex] = { ...ticket, paid: true };
			if (ticketIndex !== -1) {
				setTickets({
					...tickets,
					[ticket.ticketType]: updatedTickets,
				});
			}
			ToastWrapper("success", "Successfully add to balance");
		} else if (data) {
			ToastWrapper("error", data?.Result?.Message);
		}

		setLoading(false);
		onClose();
	};

	const onClose = () => {
		setTickets({
			sport: [],
			deposit: [],
			casino: [],
		});
		closePopup();
	};

	const scanMore = () => {
		navigate("/tickets");
		closePopup();
	};

	const handleCopyToBetslip = async () => {
		if (ticket.dataFromCasino) {
			const copyData = getCasinoCopyBetData({ casinoInfo: ticket.dataFromCasino });
			if (copyData?.gameId) {
				navigate(`/thirdPartyGame/${copyData.gameId}?action=copyTicket&betId=${ticket.dataFromCasino.CasinoTransactionId}`);
				closePopup();
			}
			return;
		}

		await copyToBetslip(ticket);
		closePopup();

		if (!Config.enableMultipleTickets) {
			setTickets({
				sport: [],
				deposit: [],
				casino: [],
			});
		}
	};

	const winAmount = getTicketWinAmount(ticket) ?? ticket.dataFromCasino?.TotalWinAmount;
	return (
		<div className="ticket-action-container">
			<ActionSquare actionName="Close" onClick={onClose} icon="icon-regular-close" />
			{Config.enableMultipleTickets && !Config.disableTicketsFlow && (
				<ActionSquare actionName="scan more" icon="icon-barcode" onClick={scanMore} />
			)}
			{isCashout(ticket) && !!sportTicketCashoutAmount && (
				<ActionSquare
					actionName={["cashout {1}", sportTicketCashoutAmount]}
					icon="icon-pay"
					onClick={cashout}
					className="detail-cashout"
					isLoading={loading}
				/>
			)}
			{isCopy(ticket) && (
				<ActionSquare actionName="copy" onClick={handleCopyToBetslip} icon="icon-action-copy" />
			)}
			{isPay(ticket, isPayable, winAmount) && (
				<ActionSquare
					actionName={["pay {1}", winAmount]}
					className="detail-cashout"
					icon="icon-pay"
					onClick={addBalance}
					isLoading={loading}
				/>
			)}
			{isSubmitBigWin(ticket, isPayable, winAmount) &&

				<ActionSquare
					actionName="Submit payout request"
					icon="icon-pay"
					onClick={submitBigWin}
					className="payout-request"
				/>
			}
		</div>
	);
};

export default TicketDetailsAction;

TicketDetailsAction.propTypes = {
	ticket: PropTypes.object,
	setTickets: PropTypes.func,
	tickets: PropTypes.object,
	cashoutAmount: PropTypes.number,
};
